import { useSelector } from 'react-redux';
import { chartApi } from '../store/api/chart.api';
import { RootState } from 'common/store';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useLocation } from 'react-router-dom';

const checkZeroValue = (endpointName: string, data: any): boolean => {
  if (!data) {
    return false;
  }

  switch (endpointName) {
    case 'getAggregate':
      if (Array.isArray(data?.data) && data.data.length === 0) {
        return true;
      }

      if (data?.groupedList?.every((group: any) => group.data.length === 0)) {
        return true;
      }

      return data?.aggregates?.every(
        (aggregate: any) => aggregate.value === 0 || aggregate.value === undefined
      ) as boolean;
    case 'getChartSeries':
      if (!data?.groupedList || data.groupedList.length === 0) {
        return true;
      }
      return !!data?.groupedList?.every((group: any) => group.data.length === 0);
    case 'getList':
      return data?.total === 0 || data?.data?.length === 0;
    case 'getGeoData':
      return !!data?.features?.every((feature: any) => feature.properties.dataCount === 0);
    default:
      return false;
  }
};

type AnyQueries = Record<
  string,
  {
    status: QueryStatus;
    endpointName?: string;
    originalArgs?: any;
    startedTimeStamp?: number;
    data?: any;
  }
>;

const EXCLUDED_WIDGET_IDS = ['ai-chat', 'elsieai-summary'];

const selectIdFromArgs = (args: any): string | null => {
  const id = args?.widgetId ?? args?.id ?? args?.widgetID ?? args?.key ?? null;

  if (id == null) {
    return null;
  }

  const idStr = String(id);
  return EXCLUDED_WIDGET_IDS.includes(idStr) ? null : idStr;
};

const createApiStatusAndIdsSelector = (
  apiSlice: { reducerPath: keyof RootState },
  queryStatus: QueryStatus,
  pageStartTime: number
) =>
  createSelector(
    (state: RootState) => (state[apiSlice.reducerPath] as { queries?: AnyQueries })?.queries ?? {},
    (queries) => {
      // Group queries by widget ID, keeping only the most recent one (by startedTimeStamp)
      // Only include queries that started after the current page mounted
      const latestQueryByWidgetId = new Map<
        string,
        { status: QueryStatus; startedTimeStamp: number }
      >();

      for (const q of Object.values(queries)) {
        if (q?.status && q?.originalArgs) {
          const currentTimestamp = q.startedTimeStamp ?? 0;

          // Skip queries that started before the current page mounted
          if (currentTimestamp < pageStartTime) {
            continue;
          }

          const id = selectIdFromArgs(q.originalArgs);
          if (id != null) {
            const existing = latestQueryByWidgetId.get(id);

            // Keep the query with the later start time
            if (!existing || currentTimestamp >= existing.startedTimeStamp) {
              latestQueryByWidgetId.set(id, {
                status: q.status,
                startedTimeStamp: currentTimestamp,
              });
            }
          }
        }
      }

      let any = false;
      const ids: string[] = [];

      for (const [id, query] of latestQueryByWidgetId) {
        if (query.status === queryStatus) {
          any = true;
          ids.push(id);
        }
      }

      return { any, ids };
    }
  );

interface ApiLoadingState {
  isLoading: boolean;
  isSuccess: boolean;
  isFailed: boolean;
  isEmptyPage: boolean;
  /** IDs of widgets per status */
  pendingWidgetIds: string[];
  successWidgetIds: string[];
  failedWidgetIds: string[];
  noDataWidgetIds: string[];
}

interface ApiTimingState {
  currentDuration: number | null; // seconds
  lastDuration: number | null; // seconds
  averageDuration: number | null; // seconds
  requestCount: number; // completed requests
}

interface UseApiLoadingStateOptions {
  /** When true, includes all queries regardless of when they started (ignores page mount time filtering) */
  includeAllQueries?: boolean;
}

interface UseApiLoadingState {
  chartApiLoadingState: ApiLoadingState;
  chartApiTimingState: ApiTimingState;
  clearTimingHistory: () => void;
}

const useApiLoadingState = (options?: UseApiLoadingStateOptions): UseApiLoadingState => {
  const { includeAllQueries = false } = options ?? {};
  const location = useLocation();

  // Track when the current page was mounted (reset on route change)
  const pageStartTimeRef = useRef<number>(Date.now());
  const previousPathnameRef = useRef<string>(location.pathname);

  useEffect(() => {
    // Update pageStartTime when pathname changes
    if (location.pathname !== previousPathnameRef.current) {
      pageStartTimeRef.current = Date.now();
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Use 0 to include all queries when includeAllQueries is true
  const pageStartTime = includeAllQueries ? 0 : pageStartTimeRef.current;

  const pendingSelector = useMemo(
    () => createApiStatusAndIdsSelector(chartApi, QueryStatus.pending, pageStartTime),
    [pageStartTime]
  );
  const successSelector = useMemo(
    () => createApiStatusAndIdsSelector(chartApi, QueryStatus.fulfilled, pageStartTime),
    [pageStartTime]
  );
  const failedSelector = useMemo(
    () => createApiStatusAndIdsSelector(chartApi, QueryStatus.rejected, pageStartTime),
    [pageStartTime]
  );
  const noDataSelector = useMemo(
    () =>
      createSelector(
        (state: RootState) =>
          (state[chartApi.reducerPath] as { queries?: AnyQueries })?.queries ?? {},
        (queries) => {
          // Group queries by widget ID, keeping only the most recent one (by startedTimeStamp)
          // Only include queries that started after the current page mounted
          const latestQueryByWidgetId = new Map<
            string,
            { status: QueryStatus; endpointName?: string; data?: any; startedTimeStamp: number }
          >();

          for (const q of Object.values(queries)) {
            if (q?.status && q?.originalArgs) {
              const currentTimestamp = q.startedTimeStamp ?? 0;

              // Skip queries that started before the current page mounted
              if (currentTimestamp < pageStartTime) {
                continue;
              }

              const id = selectIdFromArgs(q.originalArgs);
              if (id != null) {
                const existing = latestQueryByWidgetId.get(id);

                if (!existing || currentTimestamp >= existing.startedTimeStamp) {
                  latestQueryByWidgetId.set(id, {
                    status: q.status,
                    endpointName: q.endpointName,
                    data: q.data,
                    startedTimeStamp: currentTimestamp,
                  });
                }
              }
            }
          }

          let any = false;
          const ids: string[] = [];

          for (const [id, query] of latestQueryByWidgetId) {
            if (
              query.status === QueryStatus.fulfilled &&
              query.endpointName &&
              checkZeroValue(query.endpointName, query.data)
            ) {
              any = true;
              ids.push(id);
            }
          }

          return { any, ids };
        }
      ),
    [pageStartTime]
  );

  const { any: chartApiPending, ids: pendingWidgetIds } = useSelector(pendingSelector);
  const { any: chartApiSuccess, ids: successWidgetIds } = useSelector(successSelector);
  const { any: chartApiFailed, ids: failedWidgetIds } = useSelector(failedSelector);
  const { ids: noDataWidgetIds } = useSelector(noDataSelector) ?? { ids: [] };

  // Timing state with persistence
  const [timingData, setTimingData] = useState(() => {
    const stored = localStorage.getItem('chartApiTimingData');
    const defaultData = { startTime: null as number | null, completedRequests: [] as number[] };
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          startTime: null, // Always start fresh for current request
          completedRequests: parsed.completedRequests || [],
        };
      } catch {
        return defaultData;
      }
    }
    return defaultData;
  });
  const currentTimeRef = useRef<number | null>(null);

  const chartApiLoadingState = useMemo<ApiLoadingState>(
    () => ({
      isLoading: chartApiPending,
      isSuccess: chartApiSuccess,
      isFailed: chartApiFailed,
      isEmptyPage:
        noDataWidgetIds?.length > 0 && successWidgetIds.length === noDataWidgetIds?.length,
      pendingWidgetIds,
      successWidgetIds,
      failedWidgetIds,
      noDataWidgetIds,
    }),
    [
      chartApiPending,
      chartApiSuccess,
      chartApiFailed,
      pendingWidgetIds,
      successWidgetIds,
      failedWidgetIds,
      noDataWidgetIds,
    ]
  );

  // Handle timing logic
  useEffect(() => {
    const now = performance.now();

    if (chartApiPending && !timingData.startTime) {
      // API call started
      setTimingData((prev) => ({ ...prev, startTime: now }));
      currentTimeRef.current = now;
    } else if (!chartApiPending && timingData.startTime) {
      // API call completed
      const duration = now - timingData.startTime;
      setTimingData((prev) => ({
        startTime: null,
        completedRequests: [...prev.completedRequests, duration],
      }));
      currentTimeRef.current = null;
    }
  }, [chartApiPending, timingData.startTime]);

  // Persist timing data
  useEffect(() => {
    if (timingData.completedRequests.length > 0) {
      localStorage.setItem(
        'chartApiTimingData',
        JSON.stringify({ completedRequests: timingData.completedRequests })
      );
    }
  }, [timingData.completedRequests]);

  // Calculate timing metrics
  const chartApiTimingState = useMemo<ApiTimingState>(() => {
    const { startTime, completedRequests } = timingData;
    const currentDuration = startTime
      ? Math.round((performance.now() - startTime) / 10) / 100
      : null;
    const lastDuration =
      completedRequests.length > 0
        ? Math.round(completedRequests[completedRequests.length - 1] / 10) / 100
        : null;
    const averageDuration =
      completedRequests.length > 0
        ? Math.round(
            completedRequests.reduce((sum: number, d: number) => sum + d, 0) /
              completedRequests.length /
              10
          ) / 100
        : null;

    return {
      currentDuration,
      lastDuration,
      averageDuration,
      requestCount: completedRequests.length,
    };
  }, [timingData]);

  // Clear timing history
  const clearTimingHistory = useCallback(() => {
    setTimingData((prev) => ({ ...prev, completedRequests: [] }));
    localStorage.removeItem('chartApiTimingData');
  }, []);

  return useMemo(
    () => ({
      chartApiLoadingState,
      chartApiTimingState,
      clearTimingHistory,
    }),
    [chartApiLoadingState, chartApiTimingState, clearTimingHistory]
  );
};

export default useApiLoadingState;
